import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { H3Configuration } from "@/types/dataset";
import {
  AddWidgetPayload,
  WidgetConfigWithoutWidgetId,
  WidgetMap
} from "@/types/widget";

export interface WidgetIndicates {
  [baseId: string]: {
    counter: number;
    usedIndicates: number[];
  };
}

export interface WidgetHistory {
  widgets: WidgetMap;
  visualizationOrder: string[];
  widgetIndicates: WidgetIndicates;
}

export interface WidgetState {
  widgets: WidgetMap;
  visualizationOrder: string[];
  widgetIndicates: WidgetIndicates;
  history: WidgetHistory;
}

export const widgetInitialState: WidgetState = {
  widgets: {},
  visualizationOrder: [],
  widgetIndicates: {},
  history: { widgets: {}, visualizationOrder: [], widgetIndicates: {} }
};

/**
 * Registers a widget on a widget map: claims the next indicate for its dataset
 * instance and puts the generated id on top of the visualization order.
 */
const appendWidget = (
  target: WidgetHistory,
  { id, widget }: AddWidgetPayload
) => {
  if (!target.widgetIndicates[id]) {
    target.widgetIndicates[id] = { counter: 0, usedIndicates: [] };
  }

  const indicates = target.widgetIndicates[id];
  indicates.counter += 1;
  indicates.usedIndicates.push(indicates.counter);

  const widgetId = `${id}_${indicates.counter}`;
  target.widgets[widgetId] = { ...widget, widget_id: widgetId };

  if (!Array.isArray(target.visualizationOrder)) {
    target.visualizationOrder = [widgetId];
  } else if (!target.visualizationOrder.includes(widgetId)) {
    target.visualizationOrder.unshift(widgetId);
  }
};

export const widgetSlice = createSlice({
  name: "widget",
  initialState: widgetInitialState,
  reducers: {
    addWidget: (state, action: PayloadAction<AddWidgetPayload>) => {
      appendWidget(state, action.payload);
    },
    // Removes one specific widget by its full widget_id (e.g. "ds-1_2").
    // Used when the user manually removes a single card from the summary panel.
    // Prunes the removed index from `usedIndicates` and re-syncs `counter` to
    // the highest remaining suffix so future adds can reuse the top of the
    // range (e.g. add _1,_2,_3, remove _3 → next add becomes _3, not _4).
    // For middle removals the counter stays put to avoid colliding with an
    // existing suffix above it.
    removeSingleWidget: (state, action: PayloadAction<string>) => {
      const widgetId = action.payload;
      state.visualizationOrder = state.visualizationOrder.filter(
        (id) => id !== widgetId
      );
      delete state.widgets[widgetId];

      const lastUnderscore = widgetId.lastIndexOf("_");
      if (lastUnderscore === -1) return;
      const baseId = widgetId.slice(0, lastUnderscore);
      const indicate = Number(widgetId.slice(lastUnderscore + 1));
      const indicates = state.widgetIndicates[baseId];
      if (!indicates || Number.isNaN(indicate)) return;

      indicates.usedIndicates = indicates.usedIndicates.filter(
        (n) => n !== indicate
      );
      indicates.counter = indicates.usedIndicates.length
        ? Math.max(...indicates.usedIndicates)
        : 0;
    },
    // Removes all widgets that belong to a dataset by its base id (e.g. "ds-1").
    // Used when the dataset itself is removed from the map, which must evict every
    // suffixed widget (ds-1_1, ds-1_2, …) and clean up widgetIndicates entirely.
    removeWidgetFromDataset: (state, action: PayloadAction<string>) => {
      const baseId = action.payload;
      const indicates = state.widgetIndicates[baseId];

      const idsToRemove = indicates?.usedIndicates.length
        ? indicates.usedIndicates.map((n) => `${baseId}_${n}`)
        : [baseId];

      idsToRemove.forEach((widgetId) => {
        delete state.widgets[widgetId];
      });

      state.visualizationOrder = state.visualizationOrder.filter(
        (id) => !idsToRemove.includes(id)
      );

      delete state.widgetIndicates[baseId];
    },
    updateWidgetH3Config: (
      state,
      action: PayloadAction<{
        widgetId: string;
        config: H3Configuration | null;
      }>
    ) => {
      const { widgetId: baseId, config } = action.payload;
      const indicates = state.widgetIndicates[baseId];

      if (indicates?.usedIndicates.length) {
        indicates.usedIndicates.forEach((n) => {
          const id = `${baseId}_${n}`;
          if (state.widgets[id]) state.widgets[id].h3Configuration = config;
        });
      } else if (state.widgets[baseId]) {
        state.widgets[baseId].h3Configuration = config;
      }
    },
    updateWidgetGroupBy: (
      state,
      action: PayloadAction<{ widgetId: string; groupBy: string }>
    ) => {
      const { widgetId: baseId, groupBy } = action.payload;
      const indicates = state.widgetIndicates[baseId];

      if (indicates?.usedIndicates.length) {
        indicates.usedIndicates.forEach((n) => {
          const id = `${baseId}_${n}`;
          if (state.widgets[id]) state.widgets[id].group_by = groupBy;
        });
      } else if (state.widgets[baseId]) {
        state.widgets[baseId].group_by = groupBy;
      }
    },
    updateWidget: (
      state,
      action: PayloadAction<{
        widgetId: string;
        data: WidgetConfigWithoutWidgetId;
      }>
    ) => {
      const { widgetId, data } = action.payload;
      const widget = state.widgets[widgetId];
      if (!widget) return;

      state.widgets[widgetId] = {
        widget_id: widgetId,
        ...data
      };
    },
    // Renames the label of all default widgets whose dataset_id matches.
    // Called when the user renames a dataset so that system-generated widget
    // titles stay in sync with the dataset display name.
    renameDefaultWidgets: (
      state,
      action: PayloadAction<{ datasetId: string; label: string }>
    ) => {
      const { datasetId, label } = action.payload;
      Object.values(state.widgets).forEach((widget) => {
        if (widget.is_default && widget.dataset_id === datasetId) {
          widget.label = label;
        }
      });
    },
    resetWidgetState: () => widgetInitialState,
    setWidgetState: (_state, action: PayloadAction<WidgetState>) => {
      return action.payload;
    },
    // Clears the active widget state (widgets, order, indicates) without
    // touching history — used before populating analysis-run widgets so
    // pre-analysis widgets don't accumulate alongside new ones.
    clearWidgets: (state) => {
      state.widgets = {};
      state.visualizationOrder = [];
      state.widgetIndicates = {};
    },
    setWidgetHistory: (state, action: PayloadAction<WidgetHistory>) => {
      state.history = action.payload;
    },
    // Rebuilds the history snapshot from restored widgets (project/analysis
    // config open) without touching the live widget state.
    setWidgetHistoryFromWidgets: (
      state,
      action: PayloadAction<AddWidgetPayload[]>
    ) => {
      state.history = {
        widgets: {},
        visualizationOrder: [],
        widgetIndicates: {}
      };
      action.payload.forEach((payload) => appendWidget(state.history, payload));
    },
    resetWidgetToHistory: (state) => {
      state.widgets = state.history.widgets;
      state.visualizationOrder = state.history.visualizationOrder;
      state.widgetIndicates = state.history.widgetIndicates;
      state.history = {
        widgets: {},
        visualizationOrder: [],
        widgetIndicates: {}
      };
    }
  }
});

export const {
  addWidget,
  clearWidgets,
  removeWidgetFromDataset,
  removeSingleWidget,
  updateWidgetH3Config,
  updateWidgetGroupBy,
  updateWidget,
  renameDefaultWidgets,
  resetWidgetState,
  setWidgetState,
  setWidgetHistory,
  setWidgetHistoryFromWidgets,
  resetWidgetToHistory
} = widgetSlice.actions;

export const widgetReducer = widgetSlice.reducer;
